import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import emailjs from '@emailjs/browser';
import { toast } from 'sonner';

const Contact = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await emailjs.send(
                "service_ixnt4bw",     // Your Service ID
                "template_q37ldwu",    // Your Template ID
                {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    message: formData.message,
                    time: new Date().toLocaleString(),
                },
                "LcSKq-P-Rebw0Cw5c"     // Your Public Key
            );

            toast.success("Message sent successfully!");
            setFormData({ name: '', email: '', phone: '', message: '' });

        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen pt-24" data-testid="contact-page">
            <section className="relative py-20 overflow-hidden bg-gradient-to-br from-violet-900 via-purple-800 to-blue-900 text-white">
                <div className="container mx-auto px-4 md:px-8 text-center">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">Contact Us</h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">Get in touch with us - we're here to help</p>
                    </motion.div>
                </div>
            </section>

            <section className="py-24 bg-white">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="grid lg:grid-cols-2 gap-12">
                        <div>
                            <h2 className="font-outfit font-bold text-3xl text-black mb-8">Send Us a Message</h2>
                            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                                <div>
                                    <Label htmlFor="name">Your Name *</Label>
                                    <Input id="name" name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name-input" />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email Address *</Label>
                                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="contact-email-input" />
                                </div>
                                <div>
                                    <Label htmlFor="phone">Phone Number *</Label>
                                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required data-testid="contact-phone-input" />
                                </div>
                                <div>
                                    <Label htmlFor="message">Message *</Label>
                                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} data-testid="contact-message-input" />
                                </div>
                                <Button type="submit" disabled={isSubmitting} className="w-full text-white bg-red-600 hover:bg-red-400" size="lg" data-testid="contact-submit-button">
                                    <Send className="mr-2 w-5 h-5" />
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </Button>
                            </form>
                        </div>

                        <div>
                            <h2 className="font-outfit font-bold text-3xl text-black mb-8">Contact Information</h2>
                            <div className="space-y-6 mb-8">
                                <div className="flex items-start gap-4">
                                    <MapPin className="w-6 h-6 text-black shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-outfit font-semibold text-lg mb-1">Address</h3>
                                        <p className="font-dm-sans text-slate-600">Alkapuri Station Rd, Saukta Nagar, Achole, Nalasopara East, Vasai-Virar, Maharashtra 401209</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Phone className="w-6 h-6 text-black shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-outfit font-semibold text-lg mb-1">Phone</h3>
                                        <p className="font-dm-sans text-slate-600">+91 9970657588 | +91 8390447711</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Mail className="w-6 h-6 text-black shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-outfit font-semibold text-lg mb-1">Email</h3>
                                        <p className="font-dm-sans text-slate-600">info@rkenglishschool.in</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Clock className="w-6 h-6 text-black shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-outfit font-semibold text-lg mb-1">Working Hours</h3>
                                        <p className="font-dm-sans text-slate-600">Mon-Sat: 7:00 AM - 6:00 PM<br />Sun: 10:00 AM - 2:00 PM</p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-2xl overflow-hidden shadow-xl h-80">
                                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3762.8!2d72.8!3d19.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDI0JzAwLjAiTiA3MsKwNDgnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="School Location"></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;